import React from 'react';

import { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import { FolderUtil, SortingUtil, FileUtil } from '@adsk/docs-js-sdk';

import { FILE_ERROR_CODES } from './consts';
import { isMalwareDetected } from './utils';
import Folder from '../assets/Folder';
import FolderDistributed from '../assets/FolderDistributed';
import FolderDistributedNSEnforced from '../assets/FolderDistributedNSEnforced';
import FolderNSEnforced from '../assets/FolderNSEnforced';

export { FolderUtil, SortingUtil };

export const formatEntityBlock = (entity) => ({
  ...entity,
  id: entity.urn,
  title: FileUtil.isFolder(entity) ? entity.title : entity.name,
});

export const addPathsToNode = (node, startingPath = '') => {
  const currPath =
    startingPath.length === 0 ? node.title : `${startingPath}/${node.title}`;

  if (!node.fullPath) {
    node.fullPath = currPath;
  }

  node.children?.forEach((child) => addPathsToNode(child, currPath));
};

export const formatEntityItem = (entity) => {
  const isFolder = 'folderType' in entity;
  const Icon = FolderUtil.isSharedFolder(entity)
    ? entity.hasNS
      ? FolderDistributedNSEnforced
      : FolderDistributed
    : entity.hasNS
      ? FolderNSEnforced
      : Folder;
  const entityIcon = isFolder && (
    <Icon
      style={{
        marginRight: '7px',
        verticalAlign: 'middle',
      }}
    />
  );

  return {
    ...entity,
    ...(entity.parentFolder?.permissionActions && {
      parentFolder: {
        ...entity.parentFolder,
        permissionActions: entity.parentFolder.permissionActions.map((a) =>
          a.toUpperCase(),
        ),
      },
    }),
    ...('folderType' in entity && {
      hasChildren: true,
      children: entity.folders ? entity.folders.map(formatEntityItem) : [],
    }),
    fetched: !isFolder || !!(entity.children ?? []).length,
    // Use by tree component to render with icons
    name: (
      <div
        style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}
      >
        {entityIcon}
        <div style={{ maxWidth: '766px', overflow: 'hidden' }}>
          <OverflowTooltip content={entity.title || entity.name}>
            {entity.title || entity.name}
          </OverflowTooltip>
        </div>
      </div>
    ),
    // `title` & `icon` are used by list table component to render text with highlight
    title: entity.title || entity.name,
    entityIcon,
    id: entity.urn,
    iconType: isFolder ? null : 'documentEmpty',
  };
};

export const addEntities = ({ data, nodes, id, hasNext }) =>
  data.map((node) => {
    if ('children' in node && Array.isArray(node.children)) {
      const nodeWithEntities = {
        ...node,
        ...(id === node.id
          ? {
              hasMoreChildren: hasNext,
              fetched: true,
              children: [...node.children, ...nodes],
            }
          : {
              fetched: !!node.children.length,
              children: addEntities({
                data: node.children,
                nodes,
                id,
                hasNext,
              }),
            }),
      };
      return nodeWithEntities;
    }

    return node;
  });

export const isBuildPro = () => /^\/build/.test(window.location.pathname);

const getDisabledDocuments = (documents) =>
  documents
    ?.filter((document) => !!document.disabled || isMalwareDetected(document))
    .map(({ id }) => id) ?? [];

// retArray will be created on first call, and eventually returned
export const getAllDisabledDocuments = (_data, retArray = []) => {
  const ids = getDisabledDocuments(_data);
  let accumulated = [...retArray, ...ids];
  // Recursively check for children's disabled ids
  _data.forEach((val) => {
    if (val.children?.length) {
      accumulated = getAllDisabledDocuments(val.children, accumulated);
    }
  });

  return accumulated;
};

export const isFailedFile = (document) =>
  document.hasOwnProperty('code') && document.hasOwnProperty('detail');

export const getNoPermissionFiles = (files) =>
  files.filter(({ code }) => code === FILE_ERROR_CODES.ERR_NOT_ALLOWED);

export const getDeletedFiles = (files) =>
  files.filter(({ code }) => code === FILE_ERROR_CODES.ERR_RESOURCE_NOT_EXIST);
